/**
 * Composant BilanDialog - Dialogue pour ajouter/modifier une ligne de trésorerie
 */

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Wallet, TrendingUp, TrendingDown, CheckCircle } from 'lucide-react';

export const BilanDialog = ({
  open,
  onOpenChange,
  bilanLigneData,
  setBilanLigneData,
  editingBilanLigne,
  onSave,
  onCancel
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bilan-dialog">
        <DialogHeader>
          <DialogTitle className="text-emerald-600 flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            {editingBilanLigne ? 'Modifier la ligne' : 'Ajouter une ligne'}
          </DialogTitle>
          <DialogDescription>
            Ajoutez un encaissement ou un décaissement pour suivre votre trésorerie.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Type d'opération</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={bilanLigneData.type_operation === 'encaissement' ? 'default' : 'outline'}
                className={bilanLigneData.type_operation === 'encaissement' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                onClick={() => setBilanLigneData({...bilanLigneData, type_operation: 'encaissement'})}
                data-testid="btn-encaissement"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Encaissement (+)
              </Button>
              <Button
                type="button"
                variant={bilanLigneData.type_operation === 'decaissement' ? 'default' : 'outline'}
                className={bilanLigneData.type_operation === 'decaissement' ? 'bg-red-600 hover:bg-red-700' : ''}
                onClick={() => setBilanLigneData({...bilanLigneData, type_operation: 'decaissement'})}
                data-testid="btn-decaissement"
              >
                <TrendingDown className="h-4 w-4 mr-2" />
                Décaissement (-)
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bilan-libelle">Libellé *</Label>
            <Input
              id="bilan-libelle"
              placeholder="Ex: Frais de formation, Matériel..."
              value={bilanLigneData.libelle}
              onChange={(e) => setBilanLigneData({...bilanLigneData, libelle: e.target.value})}
              data-testid="input-bilan-libelle"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bilan-montant">Montant (€) *</Label>
            <Input
              id="bilan-montant"
              type="number"
              min="0"
              step="0.01"
              value={bilanLigneData.montant}
              onChange={(e) => setBilanLigneData({...bilanLigneData, montant: parseFloat(e.target.value) || 0})}
              data-testid="input-bilan-montant"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bilan-date">Date</Label>
            <Input
              id="bilan-date"
              type="date"
              value={bilanLigneData.date}
              onChange={(e) => setBilanLigneData({...bilanLigneData, date: e.target.value})}
              data-testid="input-bilan-date"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bilan-notes">Notes (optionnel)</Label>
            <Input
              id="bilan-notes"
              placeholder="Détails supplémentaires..."
              value={bilanLigneData.notes}
              onChange={(e) => setBilanLigneData({...bilanLigneData, notes: e.target.value})}
              data-testid="input-bilan-notes"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            className={bilanLigneData.type_operation === 'encaissement' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
            onClick={onSave}
            disabled={!bilanLigneData.libelle || bilanLigneData.montant <= 0}
            data-testid="btn-save-bilan"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {editingBilanLigne ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BilanDialog;
